import api from '@utils/api'
import moment from 'moment'

import { useSecurityContext } from '@/components/Navigation/SecurityContext'

import { useOrders } from '@api/useOrders'
import {
  defaultExportConfig,
  getDefaultExportConfig,
  pageConfig,
} from '@components/OrderMovements/OrderMovements/MovementDetail/BOL/Detail/constants'
import { RequestedChangesDrawer } from '@components/_SiteWide/AuditHistory/RequestedChangesDrawer'
import { GraviGrid, onExport } from '@gravitate-js/excalibrr'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import { useModelConfig } from '@hooks/useModelConfig'
import { addDays, subDays } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { defaultColumnDefs, detailsColumnDefs } from '../components/Grid/Columns/DefaultColumnDefs'
import { orderViewColumns } from '../components/Grid/Columns/OrderColumnCellrenderers'
import { DefaultActionBtns } from '../components/Grid/HeaderControls/DefaultActionBtns'
import { useOrderManagementContext } from '../context'
import { getStorageKeys } from './util'
import type { GridConfigState } from '@gravitate-js/excalibrr/dist/components/GraviGrid/hooks/useAGGridEvent'
import type { DefaultViewProps, Filters, OrderManagementGridView } from './types'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import type { DayRange } from '@/hooks/useDateRangeFilter'
import { gridViewManager } from '@/utils/grid'
import { DetailRenderer } from '../components/Grid/Columns/DetailRenderer'
import { getOrderProductRowData, getRowId, getStorageKey } from '../util'

export function DefaultView({ isCompact, storeNumber }: DefaultViewProps) {
  const [isPopoverVisible, setIsPopoverVisible] = useState('')
  const [searchParams] = useSearchParams()
  const orderNumber = useMemo(() => searchParams.get('order_number'), [searchParams])
  const storageKeys = getStorageKeys(isCompact)
  const { updateSetting, getSetting } = useUserSettings()
  const { value: orderManagementDayRange, setValue: setOMDayRange } = useLocalStorage(storageKeys.dayRange, {
    back: -3,
    ahead: 1,
  })

  const setOrderManagementDayRange = async (value: DayRange) => {
    setOMDayRange(value)
    await updateSetting(storageKeys.dayRange, value)
  }

  const [changeId, setChangeId] = useState<string | undefined>()
  const [orderStatus, setOrderStatus] = useState<string>(localStorage.getItem(storageKeys.status) || 'all')
  const [filters, setFilters] = useState<Filters>({
    ...pageConfig?.[orderStatus]?.filters,
    From: moment(
      subDays(new Date(), orderManagementDayRange.back ? Math.abs(orderManagementDayRange.back) : 3)
    ).startOf('day'),
    To: moment(addDays(new Date(), orderManagementDayRange.ahead ? orderManagementDayRange.ahead : 1)).endOf('day'),
    Stores: [],
    Order: orderNumber || undefined,
  })

  useEffect(() => {
    if (filters && !filters.Order && orderNumber) {
      searchParams.delete('order_number')
    }
  }, [filters])

  useEffect(() => {
    getSetting(storageKeys.status).then((status: string) => {
      setOrderStatus(status || 'all')
      setFilters((prevState) => ({
        ...prevState,
        ...pageConfig?.[status || 'all']?.filters,
      }))
    })
  }, [])

  useEffect(() => {
    if (storeNumber) {
      setFilters((prevState) => {
        return { ...prevState, Stores: [storeNumber] }
      })
    }
  }, [storeNumber])

  const { scopes } = useSecurityContext()
  const { config } = useModelConfig()
  const {
    gridAPIRef,
    showModal,
    setOrderHistoryStoreId,
    setAuditOrderId,
    handleOrderChange,
    triggerTPDModal,
    handleTPDDismissModal,
  } = useOrderManagementContext()

  const onStatusChange = (value: string) => {
    setFilters((prevState) => {
      const configFilters = value === 'all' ? { state: undefined, assignment: undefined } : pageConfig?.[value]?.filters
      return { ...prevState, ...configFilters }
    })
    setOrderStatus(value)
    localStorage.setItem(storageKeys.status, value)
    updateSetting(storageKeys.status, value)
  }
  const { useBackhaulTemplateQuery, uploadBackhaulMutation, updateOrdersMutation, useOrdersQuery } = useOrders()
  const { data: backhaulTemplateData } = useBackhaulTemplateQuery()

  const isCarrierUser = !!scopes?.is_carrier

  const { data: ordersData, isPending: isLoadingOrders } = useOrdersQuery(filters, isCompact)
  const hiddenFilterKeys = ['assignment', 'state', 'from', 'to', 'notification_state']
  const manualPO = useMemo(() => Boolean(ordersData?.manual_po), [ordersData])

  const { value: selectedView, setValue: setSelectedView } = useLocalStorage(
    'orderManagement::selectedView',
    'order' as OrderManagementGridView
  )

  const isByOrderView = selectedView === 'order'
  const rowData = useMemo(() => getOrderProductRowData(ordersData, isByOrderView), [ordersData, isByOrderView])
  const storageKey = getStorageKey(isCompact ? 'site-orders-compact' : 'all-order-management', isByOrderView)

  const columnDefs: ColDef<Gravitate.Order>[] = useMemo(
    () =>
      isByOrderView
        ? defaultColumnDefs(
            showModal,
            scopes,
            setOrderHistoryStoreId,
            setAuditOrderId,
            isCarrierUser,
            gridAPIRef,
            config,
            setChangeId,
            handleOrderChange,
            isCompact,
            triggerTPDModal,
            handleTPDDismissModal,
            orderStatus,
            setIsPopoverVisible,
            isPopoverVisible,
            manualPO
          )
        : detailsColumnDefs(showModal),
    [ordersData, isPopoverVisible, orderStatus, manualPO, selectedView]
  )

  const agPropsOverrides = useMemo(
    () => ({
      getRowId: (params: ICellRendererParams) => getRowId(params.data, isByOrderView),
      suppressCellSelection: true,
      frameworkComponents: orderViewColumns,
      rowGroupPanelShow: isCompact ? 'onlyWhenGrouping' : 'always',
      detailRowAutoHeight: true,
    }),
    [isCompact, isByOrderView]
  )


  return (
    <>
      <RequestedChangesDrawer
        changeId={changeId}
        setChangeId={setChangeId}
        onClose={() => setChangeId(undefined)}
        onOrderChange={handleOrderChange}
      />
      <GraviGrid
        key={storageKey}
        columnDefaultOverrides={{ editable: false }}
        controlBarProps={{
          title: pageConfig?.[orderStatus]?.title,
          serverParams: isCompact ? undefined : ordersData?.params,
          filters,
          setFilters,
          actionButtons: (
            <DefaultActionBtns
              selectedView={selectedView}
              setSelectedView={setSelectedView}
              excelOptions={
                scopes?.is_carrier
                  ? [
                      {
                        title: 'Download Results',
                        onClick: () => onExport(gridAPIRef, getDefaultExportConfig(selectedView)),
                      },
                    ]
                  : [
                      {
                        title: 'Download Results',
                        onClick: () => onExport(gridAPIRef, getDefaultExportConfig(selectedView)),
                      },
                      {
                        title: 'Orders by Site/Product',
                        downloadEP: () => api.downloadOrdersByDrops({ filter: filters }),
                        fileName: 'Orders by Site/Product.csv',
                      },
                      {
                        title: 'Download Backhaul Template',
                        downloadEP: useBackhaulTemplateQuery,
                        downloadEPData: backhaulTemplateData,
                        fileName: 'Backhauls.xlsx',
                      },
                      {
                        title: 'Upload Backhaul Orders',
                        uploadEP: scopes.create_order ? uploadBackhaulMutation.mutateAsync : undefined,
                      },
                      {
                        title: 'Download Bulk Orders',
                        downloadEP: () => api.downloadBulkOrders(filters),
                        fileName: 'Bulk Orders.xlsx',
                      },
                      {
                        title: 'Upload Bulk Orders',
                        uploadEP: scopes.create_order ? api.uploadBulkOrders : undefined,
                      },
                    ]
              }
              orderStatus={orderStatus}
              onStatusChange={onStatusChange}
              filters={filters}
              setFilters={setFilters}
              getFromStorage
              setOrderManagementDayRange={setOrderManagementDayRange}
              orderManagementDayRange={orderManagementDayRange}
              isCompact={isCompact}
            />
          ),
          defaultFilter: { From: moment(subDays(new Date(), 2)), To: moment() },
          hiddenFilterKeys: isCompact ? [...hiddenFilterKeys, 'stores'] : hiddenFilterKeys,
        }}
        agPropOverrides={agPropsOverrides}
        columnDefs={columnDefs}
        externalRef={gridAPIRef}
        storageKey={storageKey}
        loading={isLoadingOrders}
        rowData={rowData}
        onGridConfigChanged={(config: GridConfigState) => updateSetting(`gridConfig::${storageKey}`, config)}
        toolPanelWidth={260}
        enableRangeSelection={false}
        updateEP={scopes.create_order ? updateOrdersMutation.mutateAsync : undefined}
        detailCellRenderer={DetailRenderer}
        detailRowHeight={isByOrderView}
        masterDetail={isByOrderView}
        {...gridViewManager(storageKey, updateSetting)}
      />
    </>
  )
}
